import { useEffect, useState } from "react";
import { api, post, type Row } from "../api";
import { Button } from "../ui";
import {
  CALCULATOR_BOXES,
  CALCULATOR_SCREENING,
  CALCULATOR_NOTICE,
} from "../../shared/federal-calculator";
import {
  INVESTMENT_PROFILE,
  INVESTMENT_SCREENING,
  INVESTMENT_CONFIRMATIONS,
} from "../../shared/investment-calculator";
import {
  type InvestmentEntries,
  statementBoxes,
  commonStatementBoxes,
  newStatement,
  editInvestmentEntries,
  investmentEntryProblems,
  normalizedInvestments,
} from "../../shared/investment-entry";
import "./FederalCalculator.css";

const blankW2 = () =>
  Object.fromEntries([
    ["employer", ""],
    ...CALCULATOR_BOXES.map(([id]) => [id, ""]),
  ]);
const blank = (): InvestmentEntries & {
  taxYear: number;
  filingStatus: string;
  dateOfBirth: string;
  itemizedUpperBound: string;
  w2s: Record<string, string>[];
  confirmations: Record<string, boolean>;
} => ({
  taxYear: 2025,
  filingStatus: "SINGLE",
  dateOfBirth: "",
  itemizedUpperBound: "",
  w2s: [blankW2()],
  confirmations: {},
});
const dollars = (s: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(s));
const cents = (s: string) =>
  /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(s.trim())
    ? s.trim().split(".")[0] +
      "." +
      (s.trim().split(".")[1] || "").padEnd(2, "0")
    : s;
export function FederalCalculator({
  selected,
  role,
}: {
  selected: Row;
  role: string;
}) {
  const [input, setInput] = useState(blank),
    [saved, setSaved] = useState<Row | null>(null),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [available, setAvailable] = useState(false),
    [dirty, setDirty] = useState(false),
    [submitted, setSubmitted] = useState(false),
    [history, setHistory] = useState<Row[]>([]),
    [replayed, setReplayed] = useState(false);
  const writable = ["workspace_admin", "owner", "preparer"].includes(role);
  const [reviewedW2, setReviewedW2] = useState<Row | null>(null),
    [sourceRecordIds, setSourceRecordIds] = useState<string[]>([]);
  const [investmentAvailable, setInvestmentAvailable] = useState(false);
  const investment = input.profile === INVESTMENT_PROFILE,
    screening = investment ? INVESTMENT_SCREENING : CALCULATOR_SCREENING;
  const url = `/cases/${selected.id}/federal-calculator`;
  useEffect(() => {
    let active = true;
    setLoading(true);
    setAvailable(false);
    setInput(blank());
    setSaved(null);
    setHistory([]);
    setDirty(false);
    setSubmitted(false);
    setError("");
    setReplayed(false);
    setReviewedW2(null);
    setSourceRecordIds([]);
    setInvestmentAvailable(false);
    api(url)
      .then((data) => {
        if (!active) return;
        setAvailable(true);
        setInvestmentAvailable(data.investmentAvailable === true);
        setSaved(data.latest);
        setHistory(data.history);
        setReviewedW2(data.reviewedW2 || null);
        if (data.latest) {
          setInput(data.latest.input);
          setSourceRecordIds(data.latest.sourceRecordIds || []);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [url, selected.revision]);
  const change = (next: typeof input) => {
    setInput(next);
    setDirty(true);
    setReplayed(false);
    setError("");
  };
  const problems: { id: string; message: string }[] = [];
  if (!input.dateOfBirth)
    problems.push({
      id: "calc-dob",
      message:
        "Personal information — Date of birth: enter the taxpayer’s birth date.",
    });
  if (!/^(0|[1-9]\d*)\.\d{2}$/.test(cents(input.itemizedUpperBound)))
    problems.push({
      id: "calc-itemized",
      message:
        "Deductions — Potential itemized deductions: enter an amount, including zero when applicable.",
    });
  input.w2s.forEach((row, i) => {
    if (!row.employer.trim())
      problems.push({
        id: `calc-${i}-employer`,
        message: `W-2 ${i + 1} — Employer: enter an employer or statement label.`,
      });
    CALCULATOR_BOXES.forEach(([id, label]) => {
      if (!/^(0|[1-9]\d*)\.\d{2}$/.test(cents(row[id])))
        problems.push({
          id: `calc-${i}-${id}`,
          message: `W-2 ${i + 1} — ${label}: enter dollars and cents. Enter 0 if the box is blank or zero.`,
        });
    });
  });
  screening.forEach((s) => {
    if (!input.confirmations[s.id])
      problems.push({
        id: "calc-" + s.id,
        message: `Scope review — ${s.label}: confirm this statement if correct. Otherwise this calculator is not appropriate.`,
      });
  });
  problems.push(...investmentEntryProblems(input));
  const focusProblem = (id: string) => {
    const field = document.getElementById(id);
    field?.closest("details")?.setAttribute("open", "");
    field?.scrollIntoView({ block: "center", behavior: "smooth" });
    field?.focus();
  };
  const bad = (id: string) => submitted && problems.some((p) => p.id === id);
  const helper = (id: string) =>
    bad(id) ? (
      <span className="calc-error" id={id + "-error"}>
        {problems.find((p) => p.id === id)?.message}
      </span>
    ) : null;
  const calculate = async () => {
    setSubmitted(true);
    setError("");
    if (problems.length) {
      focusProblem(problems[0].id);
      return;
    }
    setBusy(true);
    try {
      const normalized = {
        ...input,
        ...normalizedInvestments(input),
        itemizedUpperBound: cents(input.itemizedUpperBound),
        w2s: input.w2s.map((w) => ({
          ...w,
          ...Object.fromEntries(
            CALCULATOR_BOXES.map(([id]) => [id, cents(w[id])]),
          ),
        })),
      };
      const result = await post(url, {
        requestId: crypto.randomUUID(),
        expectedRevision: selected.revision,
        input: normalized,
        ...(sourceRecordIds.length ? { sourceRecordIds } : {}),
      });
      setSaved(result);
      setInput(result.input);
      setSourceRecordIds(result.sourceRecordIds || []);
      setDirty(false);
      setHistory((h) => [
        {
          id: result.id,
          createdAt: result.createdAt,
          returnRevision: result.returnRevision,
        },
        ...h,
      ]);
      setReplayed(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const replay = async () => {
    setBusy(true);
    setError("");
    try {
      await post(`${url}/${saved!.id}/replay`);
      setReplayed(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (loading)
    return (
      <section className="rb-card" role="status">
        Loading federal calculator…
      </section>
    );
  return (
    <div className="federal-calculator">
      <section className="rb-card">
        <h2>Federal tax calculator</h2>
        <p>
          Calculate 2025 federal income tax and your refund from reviewed
          statements. Results are saved with this return; calculation entries do
          not replace the interview or document records.
        </p>
        <div className="calc-scope">
          <strong>Currently supported</strong>
          <p>
            Single • no dependents • ordinary W-2 wages of $50,000 to under
            $100,000 • standard deduction • refund or zero balance. Birth dates:
            January 2, 1961–January 1, 2001.
          </p>
          {investmentAvailable && (
            <p>
              Interest and dividend statements can also be entered, including
              reviewed qualified dividends, ordinary capital gain distributions
              and statement withholding. Combined taxable receipts must remain
              below $100,000. Special statement amounts are checked and cannot
              be silently ignored.
            </p>
          )}
          <p>
            Other filing statuses, unsupported income, credits, special
            deductions and balance-due returns are not supported here. This is a
            calculation worksheet, not a completed Form 1040 or an authorization
            to file. Use synthetic data while this workspace remains in
            development.
          </p>
        </div>
      </section>
      {error && (
        <section className="rb-card calc-alert" role="alert">
          <h3>Calculation needs attention</h3>
          <p>{error}</p>
        </section>
      )}
      {available && (
        <>
          {submitted && problems.length > 0 && (
            <section className="rb-card calc-alert" role="alert">
              <h3>{problems.length} items to complete or correct</h3>
              <ul>
                {problems.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => focusProblem(p.id)}>
                      {p.message}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void calculate();
            }}
          >
            <fieldset disabled={!writable || busy}>
              <section className="rb-card">
                <h3>Reviewed document amounts</h3>
                {reviewedW2?.issue ? (
                  <p role="status">{reviewedW2.issue}</p>
                ) : reviewedW2?.recordIds?.length ? (
                  <>
                    <p>
                      {reviewedW2.recordIds.length} confirmed W-2 statement(s)
                      available. Loading them replaces the W-2 amounts below,
                      without changing personal information or deduction
                      entries.
                    </p>
                    <Button
                      onClick={() => {
                        setSourceRecordIds(reviewedW2.recordIds);
                        change({
                          ...input,
                          w2s: reviewedW2.w2s,
                          confirmations: {
                            ...input.confirmations,
                            documents: false,
                          },
                        });
                      }}
                    >
                      Use reviewed W-2 amounts
                    </Button>
                  </>
                ) : (
                  <p>
                    No preparer-confirmed W-2 records are available for this
                    return. Uploading a document alone does not add its amounts.
                    You can enter amounts manually below.
                  </p>
                )}
                {sourceRecordIds.length > 0 && (
                  <>
                    <p>
                      The amounts below are linked to confirmed document
                      reviews. Make corrections in document review, then load
                      the reviewed amounts again. The server checks the
                      complete statement selection and every imported amount
                      again when calculating.
                    </p>
                  </>
                )}
              </section>
              <section className="rb-card">
                <h3>Personal information & deductions</h3>
                <div className="calc-fields">
                  <label htmlFor="calc-dob">
                    Date of birth
                    <input
                      id="calc-dob"
                      type="date"
                      value={input.dateOfBirth}
                      aria-invalid={bad("calc-dob")}
                      aria-describedby={
                        bad("calc-dob") ? "calc-dob-error" : undefined
                      }
                      onChange={(e) =>
                        change({ ...input, dateOfBirth: e.target.value })
                      }
                    />
                    {helper("calc-dob")}
                  </label>
                  <label htmlFor="calc-itemized">
                    Potential itemized deductions (upper bound)
                    <input
                      id="calc-itemized"
                      inputMode="decimal"
                      value={input.itemizedUpperBound}
                      aria-invalid={bad("calc-itemized")}
                      onChange={(e) =>
                        change({ ...input, itemizedUpperBound: e.target.value })
                      }
                    />
                    <small>
                      Total possible medical, state/local tax, mortgage
                      interest, charitable and other itemized deductions. Must
                      not exceed $15,750.
                    </small>
                    {helper("calc-itemized")}
                  </label>
                </div>
              </section>
              {input.w2s.map((row, i) => (
                <section className="rb-card" key={i}>
                  <h3>W-2 {i + 1}</h3>
                  <div className="calc-fields">
                    <label htmlFor={`calc-${i}-employer`}>
                      Employer / statement label
                      <input
                        id={`calc-${i}-employer`}
                        maxLength={120}
                        readOnly={sourceRecordIds.length > 0}
                        value={row.employer}
                        aria-invalid={bad(`calc-${i}-employer`)}
                        onChange={(e) =>
                          change({
                            ...input,
                            w2s: input.w2s.map((w, j) =>
                              j === i ? { ...w, employer: e.target.value } : w,
                            ),
                          })
                        }
                      />
                      {helper(`calc-${i}-employer`)}
                    </label>
                    {CALCULATOR_BOXES.map(([id, label]) => (
                      <label key={id} htmlFor={`calc-${i}-${id}`}>
                        {label}
                        <input
                          id={`calc-${i}-${id}`}
                          inputMode="decimal"
                          readOnly={sourceRecordIds.length > 0}
                          value={row[id]}
                          aria-invalid={bad(`calc-${i}-${id}`)}
                          onChange={(e) =>
                            change({
                              ...input,
                              w2s: input.w2s.map((w, j) =>
                                j === i ? { ...w, [id]: e.target.value } : w,
                              ),
                            })
                          }
                        />
                        {helper(`calc-${i}-${id}`)}
                      </label>
                    ))}
                  </div>
                  {input.w2s.length > 1 && (
                    <Button
                      disabled={sourceRecordIds.length > 0}
                      onClick={() =>
                        change({
                          ...input,
                          w2s: input.w2s.filter((_, j) => j !== i),
                        })
                      }
                    >
                      Remove this W-2
                    </Button>
                  )}
                </section>
              ))}
              <Button
                disabled={input.w2s.length >= 100 || sourceRecordIds.length > 0}
                onClick={() =>
                  change({ ...input, w2s: [...input.w2s, blankW2()] })
                }
              >
                Add another W-2
              </Button>
              {(investmentAvailable || investment) && (
                <section className="rb-card">
                  <h3>Interest & dividends</h3>
                  <p>
                    Enter final 2025 statements after checking them against the
                    source document. These entries are manual; uploading a 1099
                    does not automatically extract or confirm its amounts.
                  </p>
                  {(["interestStatements", "dividendStatements"] as const).map(
                    (kind) => (
                      <div key={kind}>
                        {(input[kind] || []).map((row, i) => {
                          const edit = (next: Record<string, string>) =>
                            change(
                              editInvestmentEntries(
                                input,
                                kind,
                                (input[kind] || []).map((r, j) =>
                                  j === i ? next : r,
                                ),
                              ),
                            );
                          const renderBox = ([id, label]: readonly [
                            string,
                            string,
                          ]) => (
                            <label key={id} htmlFor={`calc-${kind}-${i}-${id}`}>
                              {label}
                              <input
                                id={`calc-${kind}-${i}-${id}`}
                                inputMode="decimal"
                                value={row[id]}
                                aria-invalid={bad(`calc-${kind}-${i}-${id}`)}
                                aria-describedby={
                                  bad(`calc-${kind}-${i}-${id}`)
                                    ? `calc-${kind}-${i}-${id}-error`
                                    : undefined
                                }
                                onChange={(e) =>
                                  edit({ ...row, [id]: e.target.value })
                                }
                              />
                              {helper(`calc-${kind}-${i}-${id}`)}
                            </label>
                          );
                          return (
                            <section key={i} className="rb-card">
                              <h4>
                                {kind === "interestStatements"
                                  ? "Interest — 1099-INT"
                                  : "Dividends — 1099-DIV"}{" "}
                                {i + 1}
                              </h4>
                              <div className="calc-fields">
                                <label htmlFor={`calc-${kind}-${i}-payer`}>
                                  Payer / statement label
                                  <input
                                    id={`calc-${kind}-${i}-payer`}
                                    maxLength={120}
                                    value={row.payer}
                                    aria-invalid={bad(
                                      `calc-${kind}-${i}-payer`,
                                    )}
                                    onChange={(e) =>
                                      edit({ ...row, payer: e.target.value })
                                    }
                                  />
                                  {helper(`calc-${kind}-${i}-payer`)}
                                </label>
                                {statementBoxes(kind)
                                  .filter(([k]) =>
                                    commonStatementBoxes(kind).includes(k),
                                  )
                                  .map(renderBox)}
                              </div>
                              <details>
                                <summary>Other boxes on this statement</summary>
                                <p>
                                  Copy any other amounts below. A nonzero
                                  unsupported amount stops calculation and
                                  identifies the treatment still needed.
                                </p>
                                <div className="calc-fields">
                                  {statementBoxes(kind)
                                    .filter(
                                      ([k]) =>
                                        !commonStatementBoxes(kind).includes(k),
                                    )
                                    .map(renderBox)}
                                </div>
                              </details>
                              <div className="calc-actions">
                                <Button
                                  onClick={() =>
                                    edit(
                                      Object.fromEntries(
                                        Object.entries(row).map(([k, v]) => [
                                          k,
                                          k !== "payer" && v.trim() === ""
                                            ? "0.00"
                                            : v,
                                        ]),
                                      ),
                                    )
                                  }
                                >
                                  I checked: all remaining blank amounts are
                                  zero
                                </Button>
                                <Button
                                  onClick={() =>
                                    change(
                                      editInvestmentEntries(
                                        input,
                                        kind,
                                        (input[kind] || []).filter(
                                          (_, j) => j !== i,
                                        ),
                                      ),
                                    )
                                  }
                                >
                                  Remove this statement
                                </Button>
                              </div>
                            </section>
                          );
                        })}
                        <Button
                          disabled={
                            !investmentAvailable ||
                            (input.interestStatements?.length || 0) +
                              (input.dividendStatements?.length || 0) >=
                              100
                          }
                          onClick={() =>
                            change(
                              editInvestmentEntries(input, kind, [
                                ...(input[kind] || []),
                                newStatement(kind),
                              ]),
                            )
                          }
                        >
                          Add{" "}
                          {kind === "interestStatements"
                            ? "interest statement (1099-INT)"
                            : "dividend statement (1099-DIV)"}
                        </Button>
                      </div>
                    ),
                  )}
                  {investment && (
                    <div>
                      <h4>Statement review</h4>
                      {INVESTMENT_CONFIRMATIONS.map((s) => (
                        <div className="calc-confirm" key={s.id}>
                          <label htmlFor={"calc-investment-" + s.id}>
                            <input
                              id={"calc-investment-" + s.id}
                              type="checkbox"
                              checked={
                                input.investmentConfirmations?.[s.id] === true
                              }
                              aria-invalid={bad("calc-investment-" + s.id)}
                              onChange={(e) =>
                                change({
                                  ...input,
                                  investmentConfirmations: {
                                    ...input.investmentConfirmations,
                                    [s.id]: e.target.checked,
                                  },
                                })
                              }
                            />
                            <span>
                              <strong>{s.label}</strong>
                              <small>{s.detail}</small>
                            </span>
                          </label>
                          {helper("calc-investment-" + s.id)}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
              <section className="rb-card">
                <h3>Confirm the supported tax situation</h3>
                <p>
                  Check each statement after reviewing the source information.
                  If any statement is not true, do not use this calculator for
                  this return.
                </p>
                {screening.map((s) => (
                  <div className="calc-confirm" key={s.id}>
                    <label htmlFor={"calc-" + s.id}>
                      <input
                        id={"calc-" + s.id}
                        type="checkbox"
                        checked={input.confirmations[s.id] === true}
                        aria-invalid={bad("calc-" + s.id)}
                        onChange={(e) =>
                          change({
                            ...input,
                            confirmations: {
                              ...input.confirmations,
                              [s.id]: e.target.checked,
                            },
                          })
                        }
                      />
                      <span>
                        <strong>{s.label}</strong>
                        <small>{s.detail}</small>
                      </span>
                    </label>
                    {helper("calc-" + s.id)}
                  </div>
                ))}
              </section>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? "Calculating…" : "Calculate federal tax"}
              </Button>
            </fieldset>
          </form>
          {!writable && (
            <p>
              This worksheet is read-only. An assigned preparer or firm owner
              can calculate it.
            </p>
          )}
          {saved && (
            <section className="rb-card calc-result" aria-live="polite">
              <h2>Federal calculation results</h2>
              {(dirty || saved.stale) && (
                <p className="calc-stale" role="status">
                  {dirty
                    ? "Entries have changed. The amounts below are the previous saved result; calculate again to update them."
                    : saved.reconciliationIssue || "The return or its source documents changed or need review. Resolve document issues, review the entries and calculate again before relying on these amounts."}
                </p>
              )}
              <div className="calc-totals">
                {["15", "24", "35a"].map((n) => {
                  const line = saved.result.lines.find(
                    (l: Row) => l.line === n,
                  );
                  return (
                    <div key={n}>
                      <span>{line.label}</span>
                      <strong>{dollars(line.amount)}</strong>
                    </div>
                  );
                })}
              </div>
              <p>
                Calculated {new Date(saved.createdAt).toLocaleString()} ·{" "}
                {saved.result.notice || CALCULATOR_NOTICE}
              </p>
              {saved.result.scheduleB && (
                <p>
                  Schedule B:{" "}
                  {saved.result.scheduleB.required
                    ? "required"
                    : "not required under the confirmed facts"}
                  . Payer totals and line amounts are included in the
                  downloadable workpaper; an official Schedule B has not been
                  rendered.
                </p>
              )}
              {saved.result.lines.map((l: Row) => (
                <details key={l.line}>
                  <summary>
                    <span>
                      Line {l.line} · {l.label}
                    </span>
                    <strong>{dollars(l.amount)}</strong>
                  </summary>
                  <p>
                    {l.explanation}{" "}
                    <a href={l.authority} target="_blank" rel="noreferrer">
                      IRS instructions
                    </a>
                  </p>
                </details>
              ))}
              <div className="calc-actions">
                {dirty || saved.stale ? (
                  <Button disabled>Download worksheet</Button>
                ) : (
                  <a className="btn" href={`/api${url}/${saved.id}/worksheet`} download>
                    Download worksheet
                  </a>
                )}
                <Button
                  onClick={() => void replay()}
                  disabled={busy || dirty || saved.stale}
                >
                  Verify saved calculation
                </Button>
              </div>
              {replayed && (
                <p role="status">
                  Verified: the saved inputs reproduced the same calculation and
                  supporting evidence.
                </p>
              )}
            </section>
          )}
          {history.length > 1 && (
            <section className="rb-card">
              <h3>Calculation history</h3>
              <p>
                Previous calculations are retained securely. Each new
                calculation creates a separate record.
              </p>
              <ul>
                {history.map((h) => (
                  <li key={h.id}>
                    {new Date(h.createdAt).toLocaleString()} — return revision{" "}
                    {h.returnRevision}
                    {h.stale ? " (return has since changed)" : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
